import type { IssuesUpdatePayload } from '@atlassian/jira-bulk-operations-common';
import {
	StatusCategoryIds,
	StatusCategoryTypes,
} from '@atlassian/jira-common-constants/src/status-categories';
import {
	ASSIGNEE_TYPE,
	LABELS_TYPE,
	DUE_DATE_TYPE,
	PRIORITY_TYPE,
	FIX_VERSIONS_TYPE,
	PARENT_TYPE,
	STATUS_TYPE,
} from '@atlassian/jira-platform-field-config';
import type { CardBulkOperationsPayloadData } from '../../state/actions/cards/bulk-operations';

const getStoryPointValue = (value: unknown | null | undefined): number | null | undefined => {
	if (value === null) {
		return null;
	}
	if (typeof value === 'string' && value.trim() === '') {
		return null;
	}
	const valueAsNumber = Number(value);
	if (Number.isNaN(valueAsNumber)) {
		return undefined;
	}
	return parseFloat(valueAsNumber.toFixed(3));
};

export const getBulkOperationsDataOld = (
	data: IssuesUpdatePayload['data'],
	estimationFieldId: string | null,
) =>
	Object.keys(data).reduce((acc, key: string) => {
		if (key === ASSIGNEE_TYPE) {
			const value = data[key];
			if (value === undefined) {
				return acc;
			}
			acc.assignee =
				// TODO: update logic when automatic option is fixed
				// https://hello.atlassian.net/browse/DEE-3217
				value === null
					? null
					: {
							id: value.accountId || '',
							displayName: value.displayName,
							avatarUrl: value.avatarUrls['48x48'],
						};
		}
		if (key === LABELS_TYPE) {
			const labels = data[key];
			if (labels === undefined) {
				return acc;
			}
			const { value, action } = labels;
			acc.labels = {
				action,
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				value: (Array.isArray(value)
					? value.map((label) => label.label)
					: [value.label]) as string[],
			};
		}
		if (key === PRIORITY_TYPE) {
			const value = data[key];
			if (value === undefined) {
				return acc;
			}
			acc.priority = {
				name: value.label,
				iconUrl: value.iconUrl,
			};
		}
		if (key === DUE_DATE_TYPE) {
			const value = data[key];
			if (value === undefined) {
				return acc;
			}
			acc.dueDate = value;
		}
		if (key === FIX_VERSIONS_TYPE) {
			const fixVersions = data[key];
			if (fixVersions === undefined) {
				return acc;
			}
			const { value, action } = fixVersions;
			acc.fixVersions = {
				action,
				value: value.map((version) => version.value),
			};
		}
		if (key === estimationFieldId) {
			// Temporary solution, when estimate field is available in bulk operations
			// conditions needs to be added to check against estimationFieldId
			// value (eg. timeoriginalestimate (TIME_ESTIMATE_TYPE))
			const storyPoints = getStoryPointValue(data[key]);
			if (storyPoints !== undefined) {
				acc.estimate = {
					storyPoints,
					originalEstimate: null,
					timeRemainingEstimate: null,
				};
			}
		}
		if (key === PARENT_TYPE) {
			const value = data[key];
			if (value === undefined) {
				return acc;
			}
			acc.parentId = value ? Number(value.id) : null;
		}

		return acc;
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	}, {} as CardBulkOperationsPayloadData);

export const getBulkOperationsData = ({
	data,
	estimationFieldId,
	isCMP,
}: {
	data: IssuesUpdatePayload['data'];
	estimationFieldId: string | null;
	isCMP: boolean;
}) =>
	Object.keys(data).reduce((acc, key: string) => {
		if (key === ASSIGNEE_TYPE) {
			const value = data[key];
			if (value === undefined) {
				return acc;
			}
			acc.assignee =
				// TODO: update logic when automatic option is fixed
				// https://hello.atlassian.net/browse/DEE-3217
				value === null
					? null
					: {
							id: value.accountId || '',
							displayName: value.displayName,
							avatarUrl: value.avatarUrls['48x48'],
						};
		}
		if (key === LABELS_TYPE) {
			const labels = data[key];
			if (labels === undefined) {
				return acc;
			}
			const { value, action } = labels;
			acc.labels = {
				action,
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				value: (Array.isArray(value)
					? value.map((label) => label.label)
					: [value.label]) as string[],
			};
		}
		if (key === PRIORITY_TYPE) {
			const value = data[key];
			if (value === undefined) {
				return acc;
			}
			acc.priority = {
				name: value.label,
				iconUrl: value.iconUrl,
			};
		}
		if (key === DUE_DATE_TYPE) {
			const value = data[key];
			if (value === undefined) {
				return acc;
			}
			acc.dueDate = value;
		}
		if (key === FIX_VERSIONS_TYPE) {
			const fixVersions = data[key];
			if (fixVersions === undefined) {
				return acc;
			}
			const { value, action } = fixVersions;
			acc.fixVersions = {
				action,
				value: value.map((version) => version.value),
			};
		}
		if (key === estimationFieldId) {
			// Temporary solution, when estimate field is available in bulk operations
			// conditions needs to be added to check against estimationFieldId
			// value (eg. timeoriginalestimate (TIME_ESTIMATE_TYPE))
			const storyPoints = getStoryPointValue(data[key]);
			if (storyPoints !== undefined) {
				acc.estimate = {
					storyPoints,
					originalEstimate: null,
					timeRemainingEstimate: null,
				};
			}
		}
		if (key === PARENT_TYPE) {
			const value = data[key];
			if (value === undefined) {
				return acc;
			}
			acc.parentId = value ? Number(value.id) : null;
		}

		if (key === STATUS_TYPE) {
			const value = data[key];
			if (value !== undefined && value !== null) {
				acc.statusId = value.statusId;

				if (!isCMP) {
					acc.isDone = value.statusCategoryId === `${StatusCategoryIds[StatusCategoryTypes.DONE]}`;
				}
			}
		}

		return acc;
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	}, {} as CardBulkOperationsPayloadData);
